import React, { useState } from "react";
import {
  Card, CardHeader, CardBody, Container, Row, Col, Button, Table, Badge, Alert,
} from "reactstrap";
import axiosInstance from "../Request";
import { URL } from "../api";

// Status pill for each uploaded row's journey result.
const StatusBadge = ({ value }) => {
  const v = String(value || "").toUpperCase();
  const color = v === "SAVED" || v === "CREATED" ? "success" : v === "SKIPPED" ? "warning" : v === "ERROR" ? "danger" : "secondary";
  return <Badge color={color} pill>{v || "-"}</Badge>;
};

// Outcome pill for each payout row after the replay.
const OutcomeBadge = ({ value }) => {
  if (!value) return <span className="text-muted">-</span>;
  const v = String(value).toUpperCase();
  const color = v === "PERSONAL_LOAN" ? "success" : v === "MERCHANT" ? "info" : "secondary";
  return <Badge color={color} pill>{v}</Badge>;
};

// Peday feed (payin + payout) bulk test upload.
// Excel columns: A tran_type(payin|payout) | B mobile | C amount | D account_number | E ifsc | F upi | G merchant_code
const PersonalLoanBulkUpload = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const onFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setMsg("");
    setErr("");
  };

  const upload = async () => {
    if (!file) { setErr("Please choose an .xlsx file first."); return; }
    setUploading(true);
    setMsg("");
    setErr("");
    setSummary(null);
    setRows([]);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axiosInstance.post(URL.PERSONAL_LOAN_BULK_UPLOAD, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = res.data || {};
      setSummary({
        totalRows: data.totalRows ?? 0,
        payinSaved: data.payinSaved ?? 0,
        payoutSaved: data.payoutSaved ?? 0,
        personalLoans: data.personalLoans ?? 0,
        merchantWithdrawals: data.merchantWithdrawals ?? 0,
        mobilesReplayed: data.mobilesReplayed ?? 0,
        errors: data.errors ?? 0,
      });
      setRows(Array.isArray(data.results) ? data.results : []);
      setMsg(
        `Processed ${data.totalRows ?? 0} row(s): ${data.personalLoans ?? 0} personal loan(s), ` +
        `${data.merchantWithdrawals ?? 0} merchant withdrawal(s) across ${data.mobilesReplayed ?? 0} mobile(s).`
      );
    } catch (e) {
      console.error("Bulk personal-loan upload failed:", e);
      setErr(e?.response?.data?.error || "Upload failed. Check the file format and try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Container className="mt--7" fluid>
      <Row>
        <Col>
          <Card className="shadow">
            <CardHeader className="border-0">
              <Row className="align-items-center">
                <Col xs="8">
                  <h3 className="mb-0">Peday Feed — Bulk Payin/Payout Upload</h3>
                  <small className="text-muted">
                    Upload an Excel of payin + payout rows. Saved like the peday feed, then replayed
                    per mobile: payins open the credit line, each payout becomes a merchant withdrawal
                    (&lt; ₹40,000) or a personal loan (≥ ₹40,000). Columns: tran_type(payin|payout),
                    mobile, amount, account_number, ifsc, upi, merchant_code, utr. Customer/lead are
                    resolved by mobile — no customer_id/lead_id needed.
                  </small>
                </Col>
              </Row>
            </CardHeader>
            <CardBody>
              <Row className="align-items-end">
                <Col md="6">
                  <input
                    type="file"
                    accept=".xlsx"
                    onChange={onFile}
                    className="form-control"
                  />
                </Col>
                <Col md="3">
                  <Button color="primary" onClick={upload} disabled={uploading || !file}>
                    {uploading ? "Uploading…" : "Upload & Run Journey"}
                  </Button>
                </Col>
              </Row>

              {msg ? <Alert color="success" className="mt-3">{msg}</Alert> : null}
              {err ? <Alert color="danger" className="mt-3">{err}</Alert> : null}

              {summary ? (
                <Row className="mt-3">
                  <Col md="2"><Badge color="secondary" pill>Rows: {summary.totalRows}</Badge></Col>
                  <Col md="2"><Badge color="primary" pill>Payins: {summary.payinSaved}</Badge></Col>
                  <Col md="2"><Badge color="primary" pill>Payouts: {summary.payoutSaved}</Badge></Col>
                  <Col md="2"><Badge color="success" pill>Personal: {summary.personalLoans}</Badge></Col>
                  <Col md="2"><Badge color="info" pill>Merchant: {summary.merchantWithdrawals}</Badge></Col>
                  <Col md="2"><Badge color="danger" pill>Errors: {summary.errors}</Badge></Col>
                </Row>
              ) : null}

              {rows.length ? (
                <Table className="align-items-center table-flush mt-3" responsive>
                  <thead className="thead-light">
                    <tr>
                      <th>Row</th>
                      <th>Txn Id</th>
                      <th>Mobile</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Outcome</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r, i) => (
                      <tr key={i}>
                        <td>{r.row}</td>
                        <td>{r.txnId ?? "-"}</td>
                        <td>{r.mobile ?? "-"}</td>
                        <td>{r.tranType ?? "-"}</td>
                        <td>{r.amount ?? "-"}</td>
                        <td><StatusBadge value={r.status} /></td>
                        <td>{r.tranType === "payout" ? <OutcomeBadge value={r.outcome} /> : <span className="text-muted">-</span>}</td>
                        <td className="text-muted">{r.reason ?? ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : null}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PersonalLoanBulkUpload;
